import React, { useContext } from 'react';
import type { EffectParams, SchemaValues } from '@wix/effects-kit';
import { CustomScroll, Text } from '@wix/wix-base-ui';
import styles from '../assets/styles.scss';
import type { AnimationCustomizeProps, AnimationProgressAPI } from '../types';
import { PlayBoldSmall } from '@wix/wix-ui-icons-common/classic-editor';
import { AnimationProgress } from '../components/AnimationProgress';
import { TranslationContext } from '../context/TranslationContext';

import { AnimationCustomizeControls } from '../components/AnimationCustomizeControls';

/**
 * Automatically create customize controls from schema
 */
export const AnimationCustomize: React.FC<AnimationCustomizeProps> = ({
  trigger,
  effectName,
  schema,
  values,
  onChange,
  onPreview,
  onPreviewStop,
}) => {
  const { translate } = useContext(TranslationContext);

  const [progress, setProgress] = React.useState<AnimationProgressAPI | null>(
    null,
  );

  React.useEffect(() => {
    return () => {
      progress?.stop();
      onPreviewStop(trigger);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onProgressClick = () => {
    if (!progress?.isPlaying) {
      progress?.play(values.duration!);
      onPreview(trigger, effectName, values as EffectParams);
    } else {
      progress?.stop();
      onPreviewStop(trigger);
    }
  };

  return (
    <div className={styles.animationCustomize}>
      <CustomScroll allowOuterScroll>
        <div className={styles.animationCustomizePreview}>
          <Text
            size="medium"
            skin="standard"
            weight="thin"
            shouldTranslate={false}
          >
            {translate('label_preview')}
          </Text>
          <div
            className={styles.animationCustomizePreviewProgress}
            onClick={onProgressClick}
          >
            <AnimationProgress
              ref={setProgress}
              width={26}
              strokeWidth={2}
              angle={0}
              direction="cw"
              track={{
                strokeWidth: 2,
                opacity: 0.25,
              }}
            />
            <PlayBoldSmall width={18} height={18} />
          </div>
        </div>

        <AnimationCustomizeControls
          effectName={effectName}
          schema={schema as Record<keyof EffectParams, SchemaValues>}
          values={values}
          onChange={onChange}
        />
      </CustomScroll>
    </div>
  );
};
